import { tr } from '~/app/ui/lang';

export type NavUser = {
  isSuperAdmin: boolean;
  /** Has the AIXC deploy role and isn't a super admin: sees AIXC pages and deploys only. */
  isAixcDeployer: boolean;
};

export type NavCounts = {
  /** Requests the user can act on. */
  approvals?: number;
  /** Changes waiting for the next deployment. */
  deploy?: number;
};

export type NavItem = {
  id: string;
  label: string;
  icon: string;
  to: string;
  badge?: number;
  badgeHint?: string;
  /** Pages of the admin that belong to this item. */
  match: RegExp;
};

export type NavGroup = { id: string; label: string; items: NavItem[] };

/**
 * The sidebar, grouped by job. Who sees what is unchanged from the old top
 * navigation: presets and custom widgets for super admins; assets and
 * categories for everyone but AIXC deployers; deploy for super admins and
 * AIXC deployers; people and settings for super admins.
 */
export function navGroups(user: NavUser, counts: NavCounts = {}): NavGroup[] {
  const { isSuperAdmin, isAixcDeployer } = user;
  const canDeploy = isSuperAdmin || isAixcDeployer;
  const approvals = counts.approvals || 0;
  const deploy = counts.deploy || 0;

  const groups: NavGroup[] = [
    {
      id: 'site',
      label: tr('Site', '站点'),
      items: [
        { id: 'pages', label: tr('Pages', '页面'), icon: 'description', to: '/admin', match: /^\/admin\/?$|^\/admin\/(page|template-browser)(\/|$)/ },
        ...(!isAixcDeployer ? [{ id: 'categories', label: tr('Categories', '页面分类'), icon: 'account_tree', to: '/admin/categories', match: /^\/admin\/categories(\/|$)/ }] : [])
      ]
    },
    {
      id: 'library',
      label: tr('Library', '资源库'),
      items: [
        ...(isSuperAdmin ? [
          { id: 'widgets', label: tr('Custom widgets', '自定义组件'), icon: 'widgets', to: '/admin/widgets', match: /^\/admin\/widgets(\/|$)/ },
          { id: 'presets', label: tr('Widget presets', '预设插件'), icon: 'bookmarks', to: '/admin/presets', match: /^\/admin\/presets(\/|$)/ }
        ] : []),
        ...(!isAixcDeployer ? [{ id: 'assets', label: tr('Assets', '素材'), icon: 'perm_media', to: '/admin/assets', match: /^\/admin\/assets(\/|$)/ }] : [])
      ]
    },
    {
      id: 'publishing',
      label: tr('Publishing', '发布'),
      items: [
        {
          id: 'approvals',
          label: tr('Approvals', '审批'),
          icon: 'fact_check',
          to: '/admin/approvals',
          match: /^\/admin\/(approvals|review)(\/|$)/,
          badge: approvals || undefined,
          badgeHint: approvals ? `${approvals} waiting for you` : undefined
        },
        ...(canDeploy ? [{
          id: 'deploy',
          label: tr('Deploy', '部署'),
          icon: 'rocket_launch',
          to: '/admin/deploy',
          match: /^\/admin\/deploy(\/|$)/,
          badge: deploy || undefined,
          badgeHint: deploy ? `${deploy} ${deploy === 1 ? 'change' : 'changes'} ready to deploy` : undefined
        }] : [])
      ]
    },
    {
      id: 'admin',
      label: tr('Admin', '管理'),
      items: isSuperAdmin ? [
        { id: 'roles', label: tr('People & roles', '用户权限'), icon: 'group', to: '/admin/roles', match: /^\/admin\/(roles|role)(\/|$)/ },
        { id: 'settings', label: tr('Settings', '设置'), icon: 'settings', to: '/admin/settings', match: /^\/admin\/settings(\/|$)/ }
      ] : []
    },
    {
      // For everyone: how to point an AI tool at Genesis. AI assistants find /llms.txt from here too.
      id: 'help',
      label: tr('Help', '帮助'),
      items: [
        { id: 'ai-guide', label: tr('AI guide', 'AI 指南'), icon: 'smart_toy', to: '/admin/ai-guide', match: /^\/admin\/ai-guide(\/|$)/ },
        { id: 'connect', label: tr('Connect your AI', '连接你的 AI'), icon: 'hub', to: '/admin/connect', match: /^\/admin\/connect(\/|$)/ }
      ]
    }
  ];
  return groups.filter(g => g.items.length > 0);
}

/**
 * Screens that use the whole window and have their own way back: the page
 * editor (and its version preview), the widget builder and the review page.
 */
const FOCUS = [
  /^\/admin\/page\/(?!new(\/|$)|copy\/|versions\/)[^/]+\/?$/,
  /^\/admin\/page\/[^/]+\/preview\/[^/]+\/?$/,
  /^\/admin\/widgets\/builder\/[^/]+\/?$/,
  /^\/admin\/review\/[^/]+\/?$/
];

export const isFocusRoute = (pathname: string): boolean => FOCUS.some(r => r.test(pathname));

/** Initials for the account button: "leon.yanagida" → "LY", "Hai Li" → "HL". */
export function initials(name: string | null | undefined): string {
  const parts = String(name || '').trim().split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0].charAt(0);
  const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
  return (first + last).toUpperCase();
}
